import { useState } from 'react';
import { LockIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useDiscardGuard } from '@/shared/hooks/useDiscardGuard';

import { SecretForm } from './SecretForm';
import type { SecretFormMode, SecretFormSubmit } from './SecretForm';

export type { SecretFormMode, SecretPrefill } from './SecretForm';

/**
 * Create a new entry, or replace an existing one's value, from the Secrets
 * page.
 *
 * One component for both because the two forms share everything that can go
 * wrong — a key the service refuses, a value too large, an encryption key
 * missing on the server — and keeping two copies of that in step is how the
 * two surfaces drift apart.
 *
 * The frame only: the fields and their rules are `SecretForm`, which the
 * connection form's `CreateSecretDialog` renders too (spec 123 R1).
 */
export function SecretFormSheet({
  mode,
  open,
  isPending,
  error,
  existingKeys,
  managedKeys,
  connectedTypes,
  onConnect,
  onSubmit,
  onClearError,
  onCancel,
  askedByAiTool = false,
}: {
  mode: SecretFormMode;
  open: boolean;
  isPending: boolean;
  error: unknown;
  /** Every key already in this workspace, so a collision is caught without a
      round trip. */
  existingKeys: string[];
  /** Names a connection owns, keyed by entry name — see `SecretForm`. */
  managedKeys?: Record<string, { type: string; displayName: string }>;
  connectedTypes?: ReadonlySet<string>;
  onConnect?: (type: string) => void;
  onSubmit: (input: SecretFormSubmit) => void;
  /** Clears the server's refusal once the person edits what it complained
      about. */
  onClearError: () => void;
  onCancel: () => void;
  /** Opened by the link an AI tool's `set_secret` gave (specs 240, 243): say
      so, and where the value goes, before the person types it. */
  askedByAiTool?: boolean;
}) {
  const editing = mode.kind === 'edit';
  const entry = editing ? mode.entry : undefined;
  const prefill = editing ? undefined : mode.prefill;

  // Only whether something was typed — never the value — is held out here.
  // A successful save closes through `open`, so it never asks.
  const [dirty, setDirty] = useState(false);
  const { requestClose, dialog } = useDiscardGuard(dirty, onCancel);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (isPending) return;
          if (!next) requestClose();
        }}
      >
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {editing ? `Replace the value of ${entry?.key}` : 'Add an entry'}
            </SheetTitle>
            <SheetDescription>
              {editing
                ? 'The name and kind cannot be changed. To rename an entry, delete it and add it again.'
                : 'Secrets and variables are both given to every task this workspace runs, as environment variables named after them. The tools agents run read fixed names: GITLAB_TOKEN and GITLAB_HOST for GitLab, GH_TOKEN and GH_HOST for GitHub.'}
            </SheetDescription>
          </SheetHeader>

          {/* The form owns the column so the footer's submit button stays inside
            it. Only SheetBody scrolls, so the action never scrolls away. */}
          <SecretForm
            mode={mode}
            // What a link asked for (spec 240): seeds only, typed over freely.
            suggestedKey={prefill?.key}
            initialKind={prefill?.kind}
            initialDescription={prefill?.description}
            isPending={isPending}
            error={error}
            existingKeys={existingKeys}
            managedKeys={managedKeys}
            connectedTypes={connectedTypes}
            onConnect={onConnect}
            onSubmit={onSubmit}
            onClearError={onClearError}
            onDirtyChange={setDirty}
            body={(fields) => (
              <SheetBody className="flex flex-col gap-4">
                {askedByAiTool ? <AskedByAiToolNotice /> : null}
                {fields}
              </SheetBody>
            )}
            footer={({ canSubmit }) => (
              <SheetFooter>
                <SheetClose
                  render={<Button type="button" variant="outline" />}
                  disabled={isPending}
                >
                  Cancel
                </SheetClose>
                <Button type="submit" disabled={!canSubmit}>
                  {isPending ? 'Saving…' : editing ? 'Replace value' : 'Add'}
                </Button>
              </SheetFooter>
            )}
          />
        </SheetContent>
      </Sheet>
      {dialog}
    </>
  );
}

/**
 * Why the form opened on its own, and that the value stays out of the chat
 * (spec 243): someone arriving from their AI tool sees a sheet they did not
 * open, and is about to type a secret into it.
 */
function AskedByAiToolNotice() {
  return (
    <div
      role="note"
      className="border-border bg-muted/40 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs"
    >
      <LockIcon
        aria-hidden="true"
        className="text-muted-foreground mt-px size-4 shrink-0"
        strokeWidth={1.5}
      />
      <p className="text-foreground">
        Your AI tool asked for this. Type the value here: it goes straight to
        AI-Powered SDLC and never through the chat. The tool sees only that it
        was saved.
      </p>
    </div>
  );
}
